import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, ShoppingBag, BookOpen, Wrench, KeyRound, Package } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import Reveal from "@/components/Reveal";
import api from "@/lib/api";
import SEO from "@/components/SEO";

const CATEGORIES = [
  { key: "all", label: "All", icon: <ShoppingBag size={14} /> },
  { key: "Bundles", label: "Bundles", icon: <Package size={14} /> },
  { key: "Digital Downloads", label: "Digital Downloads", icon: <BookOpen size={14} /> },
  { key: "Toolkits", label: "Toolkits", icon: <Wrench size={14} /> },
  { key: "Courses", label: "Courses", icon: <BookOpen size={14} /> },
  { key: "Memberships", label: "Memberships", icon: <KeyRound size={14} /> },
];

export default function Academy() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("category") || "all";
  const division = (params.get("division") || "").toLowerCase();
  const [active, setActive] = useState(initial);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/products");
      setProducts(data);
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    const next = new URLSearchParams(params);
    if (active === "all") next.delete("category"); else next.set("category", active);
    setParams(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const filtered = useMemo(() => {
    let list = products;
    if (division) list = list.filter((p) => (p.division || "main").toLowerCase() === division);
    if (active !== "all") list = list.filter((p) => p.category === active);
    return list;
  }, [active, products, division]);

  const divisionLabel = { hr: "HR Consulting", continuum: "Catalyst Continuum", bh: "BH Consulting" }[division] || null;

  // Preserve branch chrome so users arriving from HR/Continuum via the top-nav
  // (e.g. "HR Tools", "Toolkits") don't get flipped back to the main BH header.
  const branch = division === "hr" ? "hr" : division === "continuum" ? "continuum" : "main";

  return (
    <SiteLayout branch={branch}>
      <SEO
        title="Catalyst Academy · Downloads, Toolkits, Courses & Memberships"
        description="Digital tools built from our real consulting engagements — editable templates, curricula, self-paced courses, and Partner Portal memberships."
        path="/academy"
      />
      {/* HERO */}
      <section
        className="grain relative"
        style={{
          background: "var(--navy)",
          color: "white",
          padding: "9vw 6vw 6vw",
        }}
        data-testid="academy-hero"
      >
        <Reveal className="max-w-4xl">
          <span className="eyebrow">
            CATALYST ACADEMY · {divisionLabel ? `${divisionLabel.toUpperCase()} STORE` : "RESOURCE STORE"}
          </span>
          <h1 className="font-display mt-5 text-[clamp(44px,5.5vw,80px)] leading-[1.04]">
            {divisionLabel ? (
              <>Tools built specifically for <em className="italic text-[var(--gold)]">{divisionLabel}</em>.</>
            ) : (
              <>Turn expertise into <em className="italic text-[var(--gold)]">scalable tools</em> your team can deploy today.</>
            )}
          </h1>
          <p className="text-neutral-300 text-[17px] leading-[1.75] mt-6 max-w-[720px]">
            {divisionLabel
              ? `Editable templates, playbooks, and reference cards from our ${divisionLabel} engagements.`
              : "Downloads, toolkits, courses, and memberships — all built from our real consulting engagements. Editable, practical, and ready to use."}
          </p>
          {divisionLabel && (
            <Link
              to="/academy"
              className="inline-flex items-center gap-1 mt-6 text-[11px] tracking-widest text-white/70 hover:text-white underline underline-offset-4"
              data-testid="academy-clear-division"
            >
              ← See the full academy store
            </Link>
          )}
        </Reveal>
      </section>

      {/* CATEGORY FILTER */}
      <section className="bg-white border-b border-[var(--line)]">
        <div className="max-w-[1300px] mx-auto px-[6vw] py-6 flex flex-wrap gap-2" data-testid="category-filter">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => setActive(c.key)}
              className={`inline-flex items-center gap-2 px-5 py-3 border text-[11px] font-bold tracking-widest transition-all ${
                active === c.key
                  ? "bg-[var(--navy)] text-white border-[var(--navy)]"
                  : "bg-white text-[var(--navy)] border-[var(--line)] hover:border-[var(--navy)]"
              }`}
              data-testid={`cat-${c.key.replace(/\s+/g, "-")}`}
            >
              {c.icon} {c.label.toUpperCase()}
            </button>
          ))}
        </div>
      </section>

      {/* PRODUCT GRID */}
      <section className="section bg-[var(--light)]">
        {loading ? (
          <div className="text-center text-[var(--muted)]">Loading catalog…</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-[1300px] mx-auto" data-testid="product-grid">
            {filtered.map((p, i) => (
              <Reveal key={p.id} delay={i * 60}>
                <Link
                  to={`/academy/${p.slug || p.id}`}
                  className="card-editorial block h-full"
                  data-testid={`product-${p.id}`}
                >
                  <div className="text-[var(--gold)] text-[10px] tracking-widest font-bold">
                    {p.category.toUpperCase()}
                  </div>
                  <h3 className="font-display text-2xl text-[var(--navy)] mt-4 mb-3">{p.name}</h3>
                  <p className="text-[var(--muted)] text-sm leading-relaxed mb-5">{p.tagline}</p>
                  <div className="flex items-end justify-between pt-5 border-t border-[var(--line)]">
                    <div>
                      <div className="text-[10px] tracking-widest text-[var(--muted)] mb-1">
                        {p.type === "membership" ? "STARTING AT" : "ONE-TIME"}
                      </div>
                      <div className="font-display text-2xl text-[var(--navy)]">
                        ${p.price.toFixed(2).replace(/\.00$/, "")}
                        {p.type === "membership" && p.period_days === 30 && <span className="text-sm text-[var(--muted)]">/mo</span>}
                        {p.type === "membership" && p.period_days >= 365 && <span className="text-sm text-[var(--muted)]">/yr</span>}
                      </div>
                    </div>
                    <span className="text-[var(--gold)] font-bold text-xs flex items-center gap-2">
                      View <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
            {filtered.length === 0 && (
              <div className="col-span-full text-center py-16 text-[var(--muted)]">
                No products in this category yet.
              </div>
            )}
          </div>
        )}
      </section>

      {/* ENTERPRISE / CONTACT STRIP */}
      <section className="bg-[var(--cream)] py-14 px-[6vw]">
        <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <span className="eyebrow-dark">ENTERPRISE LICENSES</span>
            <h3 className="font-display text-3xl text-[var(--navy)] mt-3">
              Need volume access or a custom curriculum?
            </h3>
          </div>
          <Link to="/book" className="btn btn-navy" data-testid="enterprise-cta">
            Talk to a Consultant <ArrowRight className="ml-2" size={16} />
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}
